import {ErrorInfo} from 'react'
import * as Sentry from '@sentry/react'
import axios, {AxiosError} from 'axios'
import {createBrowserHistory} from 'history'
import {EnvironmentEnum, UserInfo} from 'interfaces'
import {shouldIgnoreAPIError} from './sentryApiErrorLogging/apiErrorIgnoreRules'

const history = createBrowserHistory()

type DlpEntitlementException = {
  response?: {
    config?: {url?: string}
    request?: {responseURL?: string}
    data?: {code?: string}
  }
}

/**
 * Filters out Sentry events for DLP Dopamine entitlement errors (ERR_NOT_ENTITLED).
 * @param hint Sentry hint object containing the original exception
 * @returns true if the event should be ignored, false otherwise
 */
export const shouldIgnoreDlpDopamineEntitlement = (
  hint: Sentry.EventHint,
): boolean => {
  try {
    // Strict path pattern for the Dopamine endpoint associated with a specific file hash violation.
    const TARGET_PATTERN = /\/fe-violations\/file-hash\/[a-f0-9]+\/dopamine$/
    const ENTITLEMENT_CODE = 'ERR_NOT_ENTITLED'
    const originalException = hint?.originalException as
      | DlpEntitlementException
      | undefined
    if (!originalException) return false

    // Gather possible URL locations from axios error structure.
    const rawUrl =
      originalException?.response?.config?.url ||
      originalException?.response?.request?.responseURL ||
      ''
    if (!rawUrl) return false

    if (!TARGET_PATTERN.test(rawUrl)) return false

    // Currently we only inspect response.data.code for the entitlement indicator.
    return originalException?.response?.data?.code === ENTITLEMENT_CODE
  } catch {
    // On any unexpected shape or runtime error, default to not ignoring.
    return false
  }
}

const init = () => {
  // Skip Sentry initialization in development (localhost)
  if (process.env.REACT_APP_ENV === EnvironmentEnum.DEVELOPMENT) {
    return
  }

  // Sentry initialization with beforeSend filter using the exported helper.

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV,
    tunnel: `https://${process.env.REACT_APP_FS_RELAY_HOST}/sn-relay`,
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({history}),
      Sentry.replayIntegration({
        maskAllText: false,
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    profilesSampleRate: 1.0, // Capture 100% of the profiles
    replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    release: process.env.REACT_APP_SENTRY_RELEASE,
    sendDefaultPii: true,
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    beforeSend: (event, hint) => {
      const isDlpDopamineEventIgnored = shouldIgnoreDlpDopamineEntitlement(hint)
      // Flag to indicate whether the event should be ignored or not
      const eventShouldBeIgnored = isDlpDopamineEventIgnored
      return eventShouldBeIgnored ? null : event
    },
  })
}

const SetUserLogScope = (user: Partial<UserInfo>) => {
  Sentry.getCurrentScope()?.setUser({
    id: user.emailId,
  })
}

const LogUncaughtError = (error: Error, errorInfo: ErrorInfo) => {
  Sentry.withScope(scope => {
    scope.setExtra('Uncaught Error', errorInfo)
    Sentry.captureException(error)
  })
}

const LogError = (error: Error) => {
  Sentry.withScope(scope => {
    scope.setExtra('error', error)
    Sentry.captureException(error)
  })
}

/**
 * Logs local/non-Axios failures only. Axios errors are handled by the shared
 * interceptor via LogAPIError and must not be double-reported.
 */
const LogNonAxiosError = (error: unknown): void => {
  if (axios.isAxiosError(error)) return

  LogError(error instanceof Error ? error : new Error(String(error)))
}

/**
 * Safe metadata extracted from an Axios failure. Only these fields are ever
 * sent to Sentry. Raw config, headers, tokens, request/response bodies, and
 * arbitrary query params are intentionally excluded.
 */
export type SanitizedAPIError = {
  httpMethod?: string
  requestUrl?: string
  safeQueryParams?: {policyName?: string}
  httpStatus?: number
  axiosCode?: string
  backendErrorCode?: string
  backendErrorMessage?: string
  requestId?: string
}

type ParsedRequestUrl = Pick<
  SanitizedAPIError,
  'requestUrl' | 'safeQueryParams'
>

/**
 * Strips query strings from request URLs. Only `policyName` is extracted as a
 * separate allowlisted field; all other params (e.g. tokens) are dropped.
 */
const parseRequestUrl = (rawUrl?: string): ParsedRequestUrl => {
  if (!rawUrl) return {}

  const [pathOnly, queryString] = rawUrl.split('?', 2)
  if (!queryString) return {requestUrl: pathOnly}

  const policyName = new URLSearchParams(queryString).get('policyName')
  return {
    requestUrl: pathOnly,
    ...(policyName ? {safeQueryParams: {policyName}} : {}),
  }
}

/**
 * Backend error body shape (cloud-backend flightdeck). Errors come back as
 * `{ errors: [{ message }] }` or `{ code }`; older paths use `{ message }`.
 */
type APIErrorResponseData = {
  code?: unknown
  message?: unknown
  errors?: Array<{message?: unknown}>
}

// Cap backend message length as defense-in-depth against unexpectedly long or
// PII-bearing messages from non-flightdeck/legacy endpoints.
const MAX_BACKEND_MESSAGE_LENGTH = 200

/**
 * Reads the curated backend message from `errors[0].message`, falling back to a
 * top-level `message`. Never returns `details` or any other body content.
 */
const getBackendMessage = (data?: APIErrorResponseData): string | undefined => {
  const fromErrors = data?.errors?.[0]?.message
  const raw =
    typeof fromErrors === 'string'
      ? fromErrors
      : typeof data?.message === 'string'
      ? data.message
      : undefined
  return raw ? raw.slice(0, MAX_BACKEND_MESSAGE_LENGTH) : undefined
}

/**
 * Normalizes an Axios error into safe metadata only. Never reads headers,
 * request body, tokens, or full config beyond method and path. Query strings
 * are stripped; only allowlisted `policyName` is retained separately.
 */
const sanitizeAxiosError = (error: AxiosError): SanitizedAPIError => {
  const response = error.response
  const data = response?.data as APIErrorResponseData | undefined
  const headers = (response?.headers ?? {}) as Record<string, string>
  const {requestUrl, safeQueryParams} = parseRequestUrl(error.config?.url)

  return {
    httpMethod: error.config?.method?.toUpperCase(),
    requestUrl,
    safeQueryParams,
    httpStatus: response?.status,
    axiosCode: error.code,
    backendErrorCode: typeof data?.code === 'string' ? data.code : undefined,
    backendErrorMessage: getBackendMessage(data),
    requestId: headers['apigw-requestid'],
  }
}

/**
 * Captures an API failure in Sentry using sanitized metadata only.
 *
 * For Axios errors, a synthetic Error with a safe summary is captured so the
 * raw Axios error (config/headers/tokens/body) is never serialized by Sentry.
 * Non-Axios values fall back to standard LogError behavior.
 */
const SENTRY_UNKNOWN_API_FIELD = 'UNKNOWN'
const SENTRY_UNKNOWN_HTTP_STATUS = 'NO_STATUS'

const LogAPIError = (error: unknown): void => {
  if (!axios.isAxiosError(error)) {
    LogError(
      error instanceof Error ? error : new Error('Non-Error API failure'),
    )
    return
  }

  const sanitized = sanitizeAxiosError(error)

  // Drop expected backend product/validation/auth responses (see apiErrorIgnoreRules).
  if (shouldIgnoreAPIError(sanitized)) return

  const method = sanitized.httpMethod ?? SENTRY_UNKNOWN_API_FIELD
  const url = sanitized.requestUrl ?? SENTRY_UNKNOWN_API_FIELD
  const status = sanitized.httpStatus

  Sentry.withScope(scope => {
    scope.setExtra('apiError', sanitized)
    scope.setFingerprint([
      'api-error',
      method,
      url,
      String(status ?? sanitized.axiosCode ?? SENTRY_UNKNOWN_HTTP_STATUS),
    ])
    Sentry.captureException(
      new Error(`API ${method} ${url} failed${status ? ` (${status})` : ''}`),
    )
  })
}

export default {
  init,
  SetUserLogScope,
  LogError,
  LogNonAxiosError,
  LogAPIError,
  LogUncaughtError,
}
